/* eslint-disable no-unused-vars */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router";
import queryString from "query-string";
import { useParams, useHistory, Link } from "react-router-dom";
import axios from "axios";
import {
  Card,
  Tabs,
  Form,
  Descriptions,
  Tag,
  Typography,
  Tooltip,
  Col,
  Row,
  Input,
  Button,
  message,
  Divider,
  Table,
  Icon,
  Select,
  Upload,
  notification,
  Modal,
} from "antd";
import moment from "moment";
import { gql } from "@apollo/client";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import apiw from "../../../../utils/apiw.js";
// Constants
import { PAGE, PAGE_NAME, PAGE_SIZE, ACCESS_LEVELS } from "@utils/constants";

// Layouts
import StickyBreadcrumbPageComponent from "@layouts/stickyBreadcrumbPage/stickyBreadcrumbPage";

// Utils
import { StatusTag } from "@utils/commonComponents";
import { getTokenHeader, replaceUrlByParams } from "@utils/api";
import { getPartInventoryUploadUrl } from "@utils/env";
import { amountDisplay } from "@utils/common";
import { pricesList } from "@utils/common";
import { _uuid } from "@utils/common";
import { useAccessLevel } from "@utils/hook";

// Components
import CreateInventoryDrawer from "@components/createInventoryDrawer/createInventoryDrawer";
import CreatePurchaseInfoDrawer from "@components/createPurchaseInfoDrawer/createPurchaseInfoDrawer";
import ActivityHistoryComponent from "@components/activityHistory/activityHistory";
import CreatePriceDrawer from "@components/createPriceDrawer/createPriceDrawer";
import CustPaginationComponent from "@components/custPagination/custPagination";

//Styles
import "./partDetail.scss";
import FormItem from "antd/lib/form/FormItem";

const { TabPane } = Tabs;

const API = gql`
  query EnumList {
    warehouseList {
      id
      name
      storeLocationCode
    }
    enumInventoryStatusList {
      id
      name
    }
  }
`;

const INVENTORY_RECORD_LIST = gql`
  query InventoryRecordList(
    $id: String!
    $size: Int!
    $offset: Int!
    $status: [InventoryStatus]
    $keyword: String
    $storeLocationList: [String]
    $sort: SortInventoryRecordList
  ) {
    inventoryRecordListOnPart(
      partId: $id
      size: $size
      offset: $offset
      status: $status
      keyword: $keyword
      storeLocationList: $storeLocationList
      sort: $sort
    ) {
      id
      referenceId
      serialNumber
      status {
        id
        name
      }
      activeDate
      firstStorageDate
      warehouse {
        id
        name
        storeLocationCode
      }
      purchaseInfo {
        id
        referenceId
      }
      createDate
      displayItem
    }
  }
`;

const PURCHASE_INFO_LIST = gql`
  query PurcahseInfoList($id: ID!, $size: Int!, $offset: Int!) {
    partGet(id: $id) {
      purchaseInfo(size: $size, offset: $offset) {
        id
        referenceId
        quantity
        distributor
        distributorContact
        warranty
        funder
        purchaseDate
        purchasePrice
        document {
          key
          url
          size
          name
        }
      }
    }
  }
`;

const PRICE_LIST = gql`
  query PriceList($id: ID!) {
    partGet(id: $id) {
      itemPrice {
        id
        referenceId
        monthlyRentalPrice
        depositPrice
        serviceFee
        activeDate
        lastModifiedDate
        responsible {
          tbId
        }
      }
    }
  }
`;

const PART_GET = gql`
  query PartGet($id: ID!) {
    partGet(id: $id) {
      id
      referenceId
      productInfo {
        nameEng
        nameChi
        modelNumber
        thumbnail {
          key
          url
        }
        dimension
        brand
        netWeight
        totalWeight
        colour
        specification
        characteristicTag
        manufacturer
      }
      quantity
      leasableQuantity
      category {
        id
        name
      }
      defaultDevice {
        id
        referenceId
        productInfo {
          nameEng
          nameChi
        }
        defaultPart {
          quantity
          part {
            id
            productInfo {
              nameEng
              nameChi
            }
          }
        }
      }
      optionalDevice {
        id
        referenceId
        productInfo {
          nameEng
          nameChi
        }
        optionalPart {
          quantity
          part {
            id
            productInfo {
              nameEng
              nameChi
            }
          }
        }
      }
    }
  }
`;

const INVENTORY_RECORD_TEMPLATE_GET = gql`
  query inventoryRecordTemplateFileDownload {
    inventoryRecordTemplateFileDownload {
      key
      url
      name
      size
    }
  }
`;

const INVENTORY_RECORD_DELETE_ON_PART = gql`
  mutation inventoryRecordDeleteOnPart($id: ID) {
    inventoryRecordDeleteOnPart(id: $id)
  }
`;

const PartDetail = () => {
  const cacheSessionUUID = useRef(_uuid()).current;

  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [keyword, setKeyword] = useState("");
  const { isMatched: addPriceIsDenied } = useAccessLevel([
    ACCESS_LEVELS.ADMINISTRATION,
    ACCESS_LEVELS.REFERRALS,
    ACCESS_LEVELS.CASE_MANAGEMENT,
    ACCESS_LEVELS.OPERATION_EQUIPMENT_MANAGEMENT,
    ACCESS_LEVELS.CLERICAL_EQUIPMENT_MANAGEMENT,
    ACCESS_LEVELS.ADMIN_EQUIPMENT_MANAGEMENT,
    ACCESS_LEVELS.FINANCIAL_MANAGEMENT,
    ACCESS_LEVELS.SERVICE_TEAM,
  ]);

  const { isMatched: addPriceIsAllowed } = useAccessLevel([
    ACCESS_LEVELS.SUPERADMIN_EQUIPMENT_MANAGEMENT,
  ]);

  const { isMatched: isDenied } = useAccessLevel([
    ACCESS_LEVELS.REFERRALS,
    ACCESS_LEVELS.CASE_MANAGEMENT,
    ACCESS_LEVELS.FINANCIAL_MANAGEMENT,
    ACCESS_LEVELS.SERVICE_TEAM,
  ]);

  const { data: enumList } = useQuery(API);

  const query = useMemo(() => {
    const { page, pageSize } = queryString.parse(location.search);
    return {
      ...queryString.parse(location.search),
      page: page ? Number(page) : undefined,
      // pageSize: pageSize ? Number(pageSize) : PAGE_SIZE,
      pageSize: PAGE_SIZE,
    };
  }, [location.search]);
  const isQueryReady = !!query?.page && !!query?.pageSize;

  const [localQuery, setLocalQuery] = useState(query);

  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  const onSearch = useCallback(() => {
    console.log(
      "OnSearch",
      localQuery.tabKey,
      localQuery.page,
      localQuery.pageSize
    );
    const query = {
      tabKey: localQuery.tabKey,
      page: localQuery.page,
      pageSize: localQuery.pageSize,
    };

    localQuery.storeLocationList &&
      (query.storeLocationList = localQuery.storeLocationList);
    localQuery.status && (query.status = localQuery.status);

    history.push({
      pathname: location.pathname,
      search: queryString.stringify(query),
    });
  }, [history, localQuery, location.pathname]);

  const [inventoryDrawerVisible, setInventoryDrawerVisible] = useState(false);
  const [inventoryDrawerParams, setInventoryDrawerParams] = useState(undefined);

  const [purchaseInfoDrawerVisible, setPurchaseInfoDrawerVisible] = useState(
    false
  );
  const [purchaseInfoDrawerParams, setPurchaseInfoDrawerParams] = useState(
    undefined
  );

  const [priceDrawerVisible, setPriceDrawerVisible] = useState(false);
  const [priceDrawerParams, setPriceDrawerParams] = useState(undefined);

  const [filterVisible, setFilterVisible] = useState(false);

  const [isUploading, setIsUploading] = useState(false);

  const [deleteInventoryParams, setDeleteInventoryParams] = useState({
    visible: false,
  });

  const [
    getPart,
    { data: getPartData, loading: getPartLoading, error: getPartError },
  ] = useLazyQuery(PART_GET);
  const [
    getInventoryRecord,
    {
      data: getInventoryRecordData,
      loading: getInventoryRecordLoading,
      error: getInventoryRecordError,
    },
  ] = useLazyQuery(INVENTORY_RECORD_LIST, {
    context: {
      headers: {
        "x-cache-session": cacheSessionUUID,
      },
    },
  });
  const [
    getPurchaseInfoList,
    {
      data: getPurchaseInfoListData,
      loading: getPurchaseInfoListLoading,
      error: getPurchaseInfoListError,
    },
  ] = useLazyQuery(PURCHASE_INFO_LIST);
  const [
    getPriceList,
    {
      data: getPriceListData,
      loading: getPriceListLoading,
      error: getPriceListError,
    },
  ] = useLazyQuery(PRICE_LIST);

  const [inventoryDelete] = useMutation(INVENTORY_RECORD_DELETE_ON_PART);

  const part = useMemo(() => {
    return getPartData?.partGet;
  }, [getPartData]);

  const purchaseInfo = useMemo(() => {
    return (getPurchaseInfoListData?.partGet?.purchaseInfo ?? []).slice(
      0,
      query.pageSize
    );
  }, [getPurchaseInfoListData, query.pageSize]);

  const inventoryRecords = useMemo(() => {
    return (getInventoryRecordData?.inventoryRecordListOnPart ?? []).slice(
      0,
      query.pageSize
    );
  }, [getInventoryRecordData, query.pageSize]);

  // console.log("inventoryRecords", inventoryRecords);

  const prices = useMemo(() => {
    return pricesList(getPriceListData?.partGet?.itemPrice ?? []);
  }, [getPriceListData]);

  const isMaxPage = useMemo(() => {
    switch (query.tabKey) {
      case "purchaseInfo":
        return purchaseInfo.length < query.pageSize;
      case "quantityInfo":
        return inventoryRecords.length < query.pageSize;
      case "priceList":
        return prices.length < query.pageSize;
      default:
        return false;
    }
  }, [
    inventoryRecords.length,
    purchaseInfo.length,
    prices.length,
    query.pageSize,
    query.tabKey,
  ]);

  const refreshInventoryRecordList = useCallback(() => {
    if (query && query.page && query.pageSize)
      getInventoryRecord({
        variables: {
          id,
          size: query.pageSize + 1,
          offset: (query.page - 1) * query.pageSize,
          status: query.status,
          storeLocationList: query.storeLocationList,
          sort: "referenceId",
        },
      });
  }, [getInventoryRecord, id, query]);

  const refreshPurchaseInfoList = useCallback(() => {
    getPurchaseInfoList({
      variables: {
        id,
        size: query.pageSize + 1,
        offset: (query.page - 1) * query.pageSize,
      },
    });
  }, [getPurchaseInfoList, id, query.page, query.pageSize]);

  const refreshPriceList = useCallback(() => {
    getPriceList({
      variables: {
        id,
      },
    });
  }, [getPriceList, id]);

  useEffect(() => {
    if (id && isQueryReady) {
      switch (query.tabKey) {
        case "purchaseInfo":
          getPurchaseInfoList({
            variables: {
              id,
              size: query.pageSize + 1,
              offset: (query.page - 1) * query.pageSize,
            },
          });
          break;
        case "quantityInfo":
          getInventoryRecord({
            variables: {
              id,
              size: query.pageSize + 1,
              offset: (query.page - 1) * query.pageSize,
              status: query.status,
              keyword: keyword,
              storeLocationList: query.storeLocationList,
            },
          });
          break;
        case "priceList":
          getPriceList({
            variables: {
              id,
            },
          });
          break;
        default:
      }
    }
  }, [
    getInventoryRecord,
    getPriceList,
    getPurchaseInfoList,
    id,
    isQueryReady,
    keyword,
    query.page,
    query.pageSize,
    query.status,
    query.storeLocationList,
    query.tabKey,
  ]);

  useEffect(() => {
    if (id) {
      getPart({
        variables: {
          id,
        },
      });
    }
  }, [getPart, id]);

  const unfilledText = <Typography.Text disabled>(未填寫)</Typography.Text>;

  const inventoryInfoColumns = [
    {
      title: "零件編號",
      dataIndex: "referenceId",
      render: (referenceId, { id }) => {
        return (
          <Link
            className="link"
            to={replaceUrlByParams(PAGE.STOCK_INVENTORY_DETAIL, {
              inventoryId: id,
            })}
          >
            {referenceId}
          </Link>
        );
      },
    },
    {
      title: "序列號",
      dataIndex: "serialNumber",
    },
    {
      title: "工場",
      dataIndex: "warehouse.name",
      render: (text) => text || "-",
    },
    {
      title: "狀態",
      dataIndex: "status",
      render: (status) => <StatusTag status={status} />,
    },
    // {
    // 	title: "訂單編號",
    // 	dataIndex: "orderNo",
    // },
    {
      title: "貨物新增日期",
      dataIndex: "createDate",
      render: (createDate) =>
        createDate ? moment(createDate).format("YYYY-MM-DD") : "-",
    },
    {
      title: "首次入倉日期",
      dataIndex: "firstStorageDate",
      render: (firstStorageDate) =>
        firstStorageDate ? moment(firstStorageDate).format("YYYY-MM-DD") : "-",
    },
    {
      title: "上架日期",
      dataIndex: "activeDate",
      render: (activeDate) =>
        activeDate ? moment(activeDate).format("YYYY-MM-DD") : "-",
    },
    {
      title: "採購單號",
      dataIndex: "purchaseInfo.referenceId",
      render: (text) => <span>{text || "-"}</span>,
    },
    {
      title: "",
      render: (record) => {
        return (
          !isDenied && (
            <Button
              onClick={() => {
                setInventoryDrawerVisible(true);
                setInventoryDrawerParams({
                  productId: id,
                  ...record,
                });
              }}
              icon="edit"
            ></Button>
          )
        );
      },
    },
    {
      title: "",
      render: (record) => {
        return !isDenied && record?.status?.id === "inactive" ? (
          <Button
            type="danger"
            onClick={() => setDeleteInventoryParams({ visible: true, record })}
          >
            刪除
          </Button>
        ) : null;
      },
    },
  ];

  const purchaseInfoColumns = [
    {
      title: "採購單號",
      dataIndex: "referenceId",
    },
    {
      title: "保養年期",
      dataIndex: "warranty",
    },
    {
      title: "分銷商",
      dataIndex: "distributor",
    },
    {
      title: "分銷商電話",
      dataIndex: "distributorContact",
    },
    {
      title: "出資者",
      dataIndex: "funder",
    },
    {
      title: "購買日期",
      dataIndex: "purchaseDate",
      render: (purchaseDate) =>
        purchaseDate ? moment(purchaseDate).format("YYYY-MM-DD") : "-",
    },
    {
      title: "價格",
      dataIndex: "purchasePrice",
    },
    {
      title: "數量",
      dataIndex: "quantity",
    },
    {
      title: "收據",
      dataIndex: "document",
      render: (doc) =>
        doc &&
        doc[0]?.url && (
          <a target="_blank" rel="noopener noreferrer" href={doc[0]?.url}>
            查看
          </a>
        ),
    },
  ];

  const priceInfoColumns = [
    {
      title: "生效日期",
      dataIndex: "activeDate",
      render: (activeDate) => (activeDate ? activeDate : "-"),
    },
    {
      title: "月租價格",
      dataIndex: "monthlyRentalPrice",
      render: (amount) => <span>{amountDisplay(amount)}</span>,
    },
    {
      title: "服務成本",
      dataIndex: "serviceFee",
      render: (amount) => <span>{amountDisplay(amount)}</span>,
    },
    {
      title: "按金價格",
      dataIndex: "depositPrice",
      render: (amount) => <span>{amountDisplay(amount)}</span>,
    },
    {
      title: "更新日期",
      dataIndex: "lastModifiedDate",
      render: (lastModifiedDate) =>
        lastModifiedDate ? moment(lastModifiedDate).format("YYYY-MM-DD") : "-",
    },
    {
      title: "狀態",
      dataIndex: "status",
      render: (status) => {
        switch (status) {
          case "inactive":
            return <Tag className="tag warning">已失效</Tag>;
          case "active":
            return <Tag className="tag success">生效中</Tag>;
          case "pending":
            return <Tag className="tag info">待生效</Tag>;
          default:
        }
      },
    },
    {
      title: "",
      render: (record) => {
        if (record.status === "pending") {
          return (
            (addPriceIsAllowed || !addPriceIsDenied) && (
              <Button
                onClick={() => {
                  setPriceDrawerVisible(true);
                  setPriceDrawerParams({
                    productId: id,
                    type: "Part",
                    ...record,
                  });
                }}
                icon="edit"
              ></Button>
            )
          );
        }
      },
    },
  ];

  if (!isQueryReady) {
    history.replace({
      pathname: location.pathname,
      search: queryString.stringify({
        tabKey: query?.tabKey ?? "quantityInfo",
        page: query?.page ?? 1,
        pageSize: query?.pageSize ?? PAGE_SIZE,
      }),
    });
  }

  const defaultDeviceColumns = useMemo(() => {
    return [
      {
        title: "型號",
        dataIndex: "device.referenceId",
        render: (text, { device }) => {
          return (
            <Link
              to={`${replaceUrlByParams(PAGE.STOCK_DEVICE_DETAIL, {
                id: device?.id,
              })}`}
              target="_blank"
            >
              {text}
            </Link>
          );
        },
      },
      {
        title: "名稱",
        dataIndex: "device.productInfo.nameChi",
      },
      {
        title: "數量",
        dataIndex: "quantity",
      },
    ];
  }, []);

  const optionalDeviceColumns = useMemo(() => {
    return [
      {
        title: "型號",
        dataIndex: "device.referenceId",
        render: (text, { device }) => {
          return (
            <Link
              to={`${replaceUrlByParams(PAGE.STOCK_DEVICE_DETAIL, {
                id: device?.id,
              })}`}
              target="_blank"
            >
              {text}
            </Link>
          );
        },
      },
      {
        title: "名稱",
        dataIndex: "device.productInfo.nameChi",
      },
      {
        title: "數量",
        dataIndex: "quantity",
      },
    ];
  }, []);

  const defaultDevice = useMemo(() => {
    return part?.defaultDevice.map((device) => {
      const quantity =
        device?.defaultPart?.find(({ part }) => {
          return part.id === id;
        })?.quantity ?? 0;
      return {
        quantity,
        device,
      };
    });
  }, [part, id]);

  const optionalDevice = useMemo(() => {
    return part?.optionalDevice.map((device) => {
      const quantity =
        device?.optionalPart?.find(({ part }) => {
          return part.id === id;
        })?.quantity ?? 0;
      return {
        quantity,
        device,
      };
    });
  }, [part, id]);

  //qrcode stuffs
  const [isSearching, setIsSearching] = useState(false);
  useEffect(() => {
    if (query.storeLocationList || query.status || keyword) {
      setIsSearching(true);
    }
  }, [keyword, localQuery.storeLocationList, query]);
  const INVENTORY_RECORD_QRCODE_DOWNLOAD_ON_PART = gql`
    query InventoryRecordQrcodeDownloadOnPart(
      $id: String!
      $status: [InventoryStatus]
      $keyword: String
      $storeLocationList: [String]
    ) {
      inventoryRecordQrcodeDownloadOnPart(
        partId: $id
        keyword: $keyword
        status: $status
        storeLocationList: $storeLocationList
      ) {
        key
        url
        name
        size
      }
    }
  `;
  const [
    getInventoryRecordQrCode,
    {
      data: getInventoryRecordQrCodeData,
      loading: getInventoryRecordQrCodeLoading,
      error: getInventoryRecordQrCodeError,
    },
  ] = useLazyQuery(INVENTORY_RECORD_QRCODE_DOWNLOAD_ON_PART, {
    context: { headers: { "Content-Disposition": "attachment" } },
  });

  const refreshInventoryRecordQrcode = useCallback(async () => {
    getInventoryRecordQrCode({
      variables: {
        id,
        keyword,
        status: query.status,
        storeLocationList: query.storeLocationList,
      },
    });
  }, [
    getInventoryRecordQrCode,
    id,
    keyword,
    query.status,
    query.storeLocationList,
  ]);

  useEffect(() => {
    if (getInventoryRecordQrCodeData) {
      axios({
        url:
          getInventoryRecordQrCodeData?.inventoryRecordQrcodeDownloadOnPart.url,
        method: "GET",
        responseType: "blob",
      }).then((response) => {
        var fileURL = window.URL.createObjectURL(new Blob([response.data]));
        var fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute("download", "qrcode.zip");
        document.body.appendChild(fileLink);
        fileLink.click();
      });
    }
  }, [getInventoryRecordQrCodeData]);

  // record template download
  const [
    getInventoryRecordTemplateFile,
    {
      data: getInventoryRecordTemplateFileData,
      loading: getInventoryRecordTemplateFileLoading,
      error: getInventoryRecordTemplateFileError,
    },
  ] = useLazyQuery(INVENTORY_RECORD_TEMPLATE_GET);

  const handleTemplateDownload = useCallback(() => {
    getInventoryRecordTemplateFile();
  }, [getInventoryRecordTemplateFile]);

  useEffect(() => {
    if (getInventoryRecordTemplateFileData) {
      axios({
        url:
          getInventoryRecordTemplateFileData
            ?.inventoryRecordTemplateFileDownload.url,
        method: "GET",
        responseType: "blob",
      }).then((response) => {
        var fileURL = window.URL.createObjectURL(new Blob([response.data]));
        var fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          getInventoryRecordTemplateFileData
            ?.inventoryRecordTemplateFileDownload.name
        );
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
      });
    }
  }, [getInventoryRecordTemplateFileData]);

  const onDeleteInventory = async (record) => {
    try {
      const { data } = await inventoryDelete({ variables: { id: record?.id } });
      if (data.inventoryRecordDeleteOnPart) {
        notification.success({
          message: `成功刪除${record?.referenceId}`,
          duration: 5,
        });
        refreshInventoryRecordList();
      } else {
        notification.error({
          message: `刪除${record?.referenceId}失敗`,
          duration: 5,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const BREADCRUMB = [
    {
      path: PAGE.STOCK_PART_DETAIL,
      name: `${PAGE_NAME.STOCK_PART_DETAIL} - ${part?.referenceId ?? ""}`,
    },
  ];

  return (
    <StickyBreadcrumbPageComponent
      className="part-detail-page"
      breadcrumb={BREADCRUMB}
    >
      <Modal
        title="刪除存貨"
        visible={deleteInventoryParams.visible}
        onOk={() => {
          onDeleteInventory(deleteInventoryParams?.record);
          setDeleteInventoryParams((_) => ({ ..._, visible: false }));
        }}
        onCancel={() =>
          setDeleteInventoryParams((_) => ({ ..._, visible: false }))
        }
        okText="確定"
        cancelText="取消"
        okButtonProps={{ type: "danger" }}
      >
        確定刪除存貨 {deleteInventoryParams?.record?.referenceId} ?
      </Modal>
      <div className="status-container">
        {!isDenied && (
          <Button
            type="primary"
            onClick={() =>
              history.push(
                replaceUrlByParams(PAGE.STOCK_PART_EDIT, {
                  id: id,
                })
              )
            }
          >
            編輯
          </Button>
        )}
        {/* <Button
					type="primary"
					onClick={() =>
						history.push(PAGE.STOCK_PART)
					}
				>
					返回
				</Button> */}
      </div>
      <Row gutter={[16, 16]} style={{ marginTop: "8px" }}>
        <Col span={6}>
          {part?.productInfo?.thumbnail?.url ? (
            <img
              width="100%"
              alt="device"
              src={part?.productInfo?.thumbnail?.url ?? ""}
            />
          ) : (
            "目前沒有圖片"
          )}
        </Col>
        <Col span={18}>
          <Descriptions layout="vertical">
            <Descriptions.Item label="子類別">
              {part?.category?.name ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="零件英文名稱">
              {part?.productInfo?.nameEng ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="零件中文名稱">
              {part?.productInfo?.nameChi ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="零件型號">
              {part?.productInfo.modelNumber ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="淨重（公斤）">
              {part?.productInfo.netWeight ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="總重量（公斤）">
              {part?.productInfo.totalWeight ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="尺寸">
              {part?.productInfo?.dimension?.length > 0
                ? part?.productInfo?.dimension.map((dimension) => (
                    <Tag key={dimension}>{dimension}</Tag>
                  ))
                : unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="顏色">
              {part?.productInfo?.colour?.length > 0
                ? part?.productInfo?.colour.map((colour) => (
                    <Tag key={colour}>{colour}</Tag>
                  ))
                : unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="品牌">
              {part?.productInfo?.brand ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="生產廠家">
              {part?.productInfo?.manufacturer ?? unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="產品特點">
              {part?.productInfo?.characteristicTag?.length > 0
                ? part?.productInfo?.characteristicTag.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))
                : unfilledText}
            </Descriptions.Item>
            <Descriptions.Item label="存量">
              {part?.leasableQuantity ?? 0}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
      <Divider />
      <Tabs
        activeKey={query.tabKey}
        onChange={(tabKey) => {
          history.replace({
            pathname: location.pathname,
            search: queryString.stringify({
              tabKey,
            }),
          });
        }}
      >
        <TabPane className="section" tab="生產及採購資料" key="purchaseInfo">
          <div className="toolbar">
            <div className="left">
              {!isDenied && (
                <Button
                  className="add-btn"
                  type="primary"
                  onClick={() => {
                    setPurchaseInfoDrawerVisible(true);
                    setPurchaseInfoDrawerParams({
                      productId: id,
                    });
                  }}
                >
                  增加採購單
                </Button>
              )}
            </div>
            <div className="right">
              <CustPaginationComponent
                isMinPage={query.page === 1}
                isMaxPage={isMaxPage}
                page={query.page}
                // pageSize={query.pageSize || PAGE_SIZE}
                pageSize={PAGE_SIZE}
                onPageChange={(page) => {
                  history.push({
                    pathname: location.pathname,
                    search: queryString.stringify({
                      ...query,
                      page,
                    }),
                  });
                }}
              />
            </div>
          </div>
          <Table
            loading={getPurchaseInfoListLoading}
            pagination={false}
            rowKey="id"
            columns={purchaseInfoColumns}
            dataSource={purchaseInfo}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  if (isDenied) return;
                  setPurchaseInfoDrawerVisible(true);
                  setPurchaseInfoDrawerParams({
                    productId: id,
                    ...record,
                  });
                },
              };
            }}
            scroll={{x:"max-content"}}
          />
        </TabPane>
        <TabPane
          className="section"
          tab={`數量及存倉資料（${part?.leasableQuantity ?? 0})`}
          key="quantityInfo"
        >
          <div className="toolbar">
            <div className="left">
              {!isDenied && (
                <Button
                  className="add-btn"
                  type="primary"
                  onClick={() => {
                    setInventoryDrawerVisible(true);
                    setInventoryDrawerParams({
                      productId: id,
                    });
                  }}
                >
                  增加存貨
                </Button>
              )}
              <Input.Search
                className="search"
                placeholder="搜尋"
                value={keyword}
                onChange={({ target: { value } }) => setKeyword(value)}
                onSearch={() =>
                  history.push({
                    pathname: location.pathname,
                    search: queryString.stringify({
                      ...query,
                      keyword,
                    }),
                  })
                }
              />
              <Button
                className="filter-btn"
                type="primary"
                onClick={() => setFilterVisible((v) => !v)}
              >
                <Icon type="filter" theme="filled" />
                {!filterVisible ? "篩選" : "取消篩選"}
              </Button>
              {!isDenied && (
                <>
                  <Upload
                    className="import-xls-btn"
                    type="priamry"
                    headers={{
                      "x-token": getTokenHeader(),
                      "X-Requested-With": null,
                    }}
                    data={{
                      productId: id,
                    }}
                    onChange={(info) => {
                      if (info.file.status === "uploading") {
                        setIsUploading(true);
                        return;
                      }
                      if (info.file.status === "done") {
                        refreshInventoryRecordList();
                      }
                      if (info.file.status === "error") {
                        if (info.file.response?.error?.message)
                          notification.error({
                            message: info.file.response?.error?.message,
                          });
                        else
                          notification.error({
                            message: "上載失敗，請檢查列表內容並重試",
                          });
                      }
                      setIsUploading(false);
                    }}
                    action={getPartInventoryUploadUrl()}
                    showUploadList={false}
                  >
                    <Tooltip title="上載存貨列表">
                      <Button icon="upload" loading={isUploading} />
                    </Tooltip>
                  </Upload>
                  <Tooltip title="下載存貨列表範本">
                    <Button
                      icon="download"
                      className="export-template-btn"
                      onClick={handleTemplateDownload}
                    ></Button>
                  </Tooltip>
                  <Tooltip title="批量下載 QR Code">
                    <Button
                      disabled={!isSearching}
                      icon="qrcode"
                      className="import-xls-btn"
                      type="priamry"
                      onClick={refreshInventoryRecordQrcode}
                    ></Button>
                  </Tooltip>
                </>
              )}
            </div>
            <div className="right">
              <CustPaginationComponent
                isMinPage={query.page === 1}
                isMaxPage={isMaxPage}
                page={query.page}
                // pageSize={query.pageSize || PAGE_SIZE}
                pageSize={PAGE_SIZE}
                onPageChange={(page) => {
                  history.push({
                    pathname: location.pathname,
                    search: queryString.stringify({
                      ...query,
                      page,
                    }),
                  });
                }}
              />
            </div>
          </div>
          {filterVisible && (
            <div className="filter">
              <div className="filter-container">
                <FormItem label="存倉位置">
                  {/* <Input
										style={{ width: 200 }}
										value={localQuery?.storeLocation}
										onChange={({ target: { value } }) =>
											setLocalQuery((localQuery) => ({
												...localQuery,
												storeLocation: value,
											}))
										}
									/> */}
                  <Select
                    mode="multiple"
                    style={{ width: 200 }}
                    value={localQuery?.storeLocationList}
                    onChange={(value) =>
                      setLocalQuery((localQuery) => ({
                        ...localQuery,
                        storeLocationList: value,
                      }))
                    }
                  >
                    {(enumList?.warehouseList ?? []).map((location) => (
                      <Select.Option
                        value={location.storeLocationCode}
                        key={location.id}
                      >
                        {location.name}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem label="狀態">
                  <Select
                    mode="multiple"
                    style={{ width: 200 }}
                    value={localQuery?.status}
                    onChange={(value) =>
                      setLocalQuery((localQuery) => ({
                        ...localQuery,
                        status: value,
                      }))
                    }
                  >
                    {enumList?.enumInventoryStatusList.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </div>
              <div className="footer-container">
                <Button
                  type="secondary"
                  onClick={() => {
                    setLocalQuery((localQuery) => ({
                      ...localQuery,
                      status: undefined,
                      storeLocationList: undefined,
                    }));
                  }}
                >
                  重設
                </Button>
                <Button type="primary" onClick={onSearch}>
                  搜尋
                </Button>
              </div>
            </div>
          )}
          <Table
            loading={getInventoryRecordLoading}
            pagination={false}
            rowKey="id"
            columns={inventoryInfoColumns}
            dataSource={inventoryRecords}
            scroll={{x:"max-content"}}
            // rowSelection={{
            // 	type: "checkbox",
            // 	selectedRowKeys: selectedInventory.map((x) => x.id),
            // 	onChange: (selectedRowKeys, selected) => {
            // 		setSelectedInventory(selected);
            // 	},
            // }}
          />
        </TabPane>
        <TabPane className="section" tab="價格資料" key="priceList">
          <div className="toolbar">
            <div className="left">
              {(addPriceIsAllowed || !addPriceIsDenied) && (
                <Button
                  className="add-btn"
                  type="primary"
                  onClick={() => {
                    setPriceDrawerVisible(true);
                    setPriceDrawerParams({
                      productId: id,
                      type: "Part",
                    });
                  }}
                >
                  增加價格
                </Button>
              )}
            </div>
            <div className="right">
              <CustPaginationComponent
                isMinPage={query.page === 1}
                isMaxPage={isMaxPage}
                page={query.page}
                // pageSize={query.pageSize || PAGE_SIZE}
                pageSize={PAGE_SIZE}
                onPageChange={(page) => {
                  history.push({
                    pathname: location.pathname,
                    search: queryString.stringify({
                      ...query,
                      page,
                    }),
                  });
                }}
              />
            </div>
          </div>
          <Table
            loading={getPriceListLoading}
            pagination={false}
            rowKey="id"
            columns={priceInfoColumns}
            dataSource={prices}
            scroll={{x:"max-content"}}
          />
        </TabPane>
        <TabPane className="section" tab="其他適用貨物" key="parts">
          {/* <Descriptions layout="vertical">
						<Descriptions.Item label="預設貨物型號">
							{(part?.defaultDevice ?? []).map(
								(device, index) => (
									<Typography.Text key={index}>
										<Link
											to={replaceUrlByParams(PAGE.STOCK_DEVICE_DETAIL, { id: device.id })}
											target='_blank'
										>
										{device?.productInfo?.nameChi}
										</Link>
										(數量：
										{device?.defaultPart?.find(
											({ part }) => part.id === id
										)?.quantity ?? 0}
										)
										<br />
									</Typography.Text>
								)
							)}
						</Descriptions.Item>
						<Descriptions.Item label="額外貨物型號">
							{(part?.optionalDevice ?? []).map(
								(device, index) => (
									<Typography.Text key={index}>
										<Link
											to={replaceUrlByParams(PAGE.STOCK_DEVICE_DETAIL, { id: device.id })}
											target='_blank'
										>
										{device?.productInfo?.nameChi}
										</Link>
										(數量：
										{device?.optionalPart?.find(
											({ part }) => part.id === id
										)?.quantity ?? 0}
										)
										<br />
									</Typography.Text>
								)
							)}
						</Descriptions.Item>
					</Descriptions> */}
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="預設貨物">
                <Table
                  rowKey={(row) => row.device.id}
                  pagination={false}
                  dataSource={defaultDevice}
                  columns={defaultDeviceColumns}
                  scroll={{x:"max-content"}}
                />
              </Card>
            </Col>
            <Col span={24}>
              <Card title="額外貨物">
                <Table
                  rowKey={(row) => row.device.id}
                  pagination={false}
                  dataSource={optionalDevice}
                  columns={optionalDeviceColumns}
                  scroll={{x:"max-content"}}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
        <TabPane className="section" tab="活動記錄" key="activityLog">
          <ActivityHistoryComponent id={id} type={"Part"} />
        </TabPane>
      </Tabs>
      {inventoryDrawerVisible && (
        <CreateInventoryDrawer
          productType="part"
          onClose={() => {
            setInventoryDrawerVisible(false);
            refreshInventoryRecordList();
          }}
          defaultValues={inventoryDrawerParams}
        />
      )}
      {purchaseInfoDrawerVisible && (
        <CreatePurchaseInfoDrawer
          onClose={() => {
            setPurchaseInfoDrawerVisible(false);
            refreshPurchaseInfoList();
          }}
          productType="part"
          defaultValues={purchaseInfoDrawerParams}
        />
      )}
      {priceDrawerVisible && (
        <CreatePriceDrawer
          onClose={() => {
            setPriceDrawerVisible(false);
            refreshPriceList();
          }}
          defaultValues={priceDrawerParams}
        />
      )}
    </StickyBreadcrumbPageComponent>
  );
};

export default PartDetail;
